import { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { api } from '../services/api';
import type { Message, AngleTodo, AngleResource } from '@attack100/shared';

interface AngleChatInterfaceProps {
  angleId: number;
  angleTitle: string;
  angleDescription: string;
  onClose: () => void;
}

type TabType = 'chat' | 'todos' | 'resources';

export function AngleChatInterface({ angleId, angleTitle, angleDescription, onClose }: AngleChatInterfaceProps) {
  const [activeTab, setActiveTab] = useState<TabType>('chat');

  // Chat state
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  // Todos state
  const [todos, setTodos] = useState<AngleTodo[]>([]);
  const [todoInput, setTodoInput] = useState('');
  const [todosLoading, setTodosLoading] = useState(false);

  // Resources state
  const [resources, setResources] = useState<AngleResource[]>([]);
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const [showResourceForm, setShowResourceForm] = useState(false);
  const [resourceForm, setResourceForm] = useState({
    type: 'note' as 'link' | 'note' | 'file',
    title: '',
    content: '',
    url: ''
  });

  const [initialLoading, setInitialLoading] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadData();
  }, [angleId]);

  useEffect(() => {
    if (activeTab === 'chat') {
      scrollToBottom();
    }
  }, [messages, activeTab]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadData = async () => {
    try {
      const [chatHistory, todosList, resourcesList] = await Promise.all([
        api.getChatHistory(angleId),
        api.getTodos(angleId),
        api.getResources(angleId)
      ]);

      setMessages(chatHistory.messages);
      setTodos(todosList.todos);
      setResources(resourcesList.resources);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setInitialLoading(false);
    }
  };

  // Chat handlers
  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userContent = input.trim();
    setInput('');
    setLoading(true);

    try {
      const response = await api.sendMessage(angleId, { content: userContent });
      setMessages([...messages, response.user_message, response.assistant_message]);
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Todo handlers
  const handleAddTodo = async () => {
    if (!todoInput.trim() || todosLoading) return;

    setTodosLoading(true);
    try {
      const newTodo = await api.createTodo(angleId, { content: todoInput.trim() });
      setTodos([...todos, newTodo]);
      setTodoInput('');
    } catch (error) {
      console.error('Failed to create todo:', error);
    } finally {
      setTodosLoading(false);
    }
  };

  const handleToggleTodo = async (todo: AngleTodo) => {
    try {
      const updated = await api.updateTodo(todo.id, { completed: !todo.completed });
      setTodos(todos.map(t => t.id === todo.id ? updated : t));
    } catch (error) {
      console.error('Failed to toggle todo:', error);
    }
  };

  const handleDeleteTodo = async (todoId: number) => {
    try {
      await api.deleteTodo(todoId);
      setTodos(todos.filter(t => t.id !== todoId));
    } catch (error) {
      console.error('Failed to delete todo:', error);
    }
  };

  // Resource handlers
  const handleAddResource = async () => {
    if (!resourceForm.title.trim() || resourcesLoading) return;

    setResourcesLoading(true);
    try {
      const newResource = await api.createResource(angleId, {
        type: resourceForm.type,
        title: resourceForm.title.trim(),
        content: resourceForm.content.trim() || undefined,
        url: resourceForm.url.trim() || undefined
      });
      setResources([newResource, ...resources]);
      setResourceForm({ type: 'note', title: '', content: '', url: '' });
      setShowResourceForm(false);
    } catch (error) {
      console.error('Failed to create resource:', error);
    } finally {
      setResourcesLoading(false);
    }
  };

  const handleDeleteResource = async (resourceId: number) => {
    try {
      await api.deleteResource(resourceId);
      setResources(resources.filter(r => r.id !== resourceId));
    } catch (error) {
      console.error('Failed to delete resource:', error);
    }
  };

  if (initialLoading) {
    return (
      <>
        <div className="workspace-backdrop" onClick={onClose} />
        <div className="workspace-interface">
          <div className="workspace-header">
            <h3>{angleTitle}</h3>
            <button onClick={onClose} className="close-btn">✕</button>
          </div>
          <div className="workspace-loading">Loading workspace...</div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="workspace-backdrop" onClick={onClose} />
      <div className="workspace-interface">
      <div className="workspace-header">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
            <h3>{angleTitle}</h3>
            <span className="badge badge-primary" style={{ fontSize: '0.7rem' }}>
              Llama 3.1 8B
            </span>
          </div>
          <p className="workspace-description">{angleDescription}</p>
        </div>
        <button onClick={onClose} className="close-btn">✕</button>
      </div>

      {/* Tabs */}
      <div className="workspace-tabs">
        <button
          className={`workspace-tab ${activeTab === 'chat' ? 'active' : ''}`}
          onClick={() => setActiveTab('chat')}
        >
          💬 Chat {messages.length > 0 && `(${messages.length})`}
        </button>
        <button
          className={`workspace-tab ${activeTab === 'todos' ? 'active' : ''}`}
          onClick={() => setActiveTab('todos')}
        >
          ✓ Todos {todos.length > 0 && `(${todos.filter(t => !t.completed).length}/${todos.length})`}
        </button>
        <button
          className={`workspace-tab ${activeTab === 'resources' ? 'active' : ''}`}
          onClick={() => setActiveTab('resources')}
        >
          📎 Resources {resources.length > 0 && `(${resources.length})`}
        </button>
      </div>

      {/* Chat Tab */}
      {activeTab === 'chat' && (
        <>
          <div className="workspace-content">
            {messages.length === 0 ? (
              <div className="workspace-empty">
                <p>Start a conversation about this angle.</p>
                <p className="hint">Ask questions, brainstorm ideas, or request next steps.</p>
              </div>
            ) : (
              messages.map((msg) => (
                <div key={msg.id} className={`message message-${msg.role}`}>
                  <div className="message-content">
                    <ReactMarkdown>{msg.content}</ReactMarkdown>
                  </div>
                  <div className="message-time">
                    {new Date(msg.created_at).toLocaleTimeString()}
                  </div>
                </div>
              ))
            )}
            {loading && (
              <div className="message message-assistant">
                <div className="message-content typing">AI is thinking...</div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="workspace-input-area">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message... (Enter to send, Shift+Enter for new line)"
              disabled={loading}
              rows={3}
            />
            <button onClick={handleSend} disabled={!input.trim() || loading}>
              Send
            </button>
          </div>
        </>
      )}

      {/* Todos Tab */}
      {activeTab === 'todos' && (
        <>
          <div className="workspace-content">
            <div className="workspace-input-area" style={{ borderBottom: '1px solid var(--gray-200)', marginBottom: '1rem' }}>
              <input
                type="text"
                value={todoInput}
                onChange={(e) => setTodoInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddTodo()}
                placeholder="Add a new todo..."
                disabled={todosLoading}
                style={{ flex: 1, padding: '0.75rem', border: '1px solid var(--gray-300)', borderRadius: '0.5rem' }}
              />
              <button onClick={handleAddTodo} disabled={!todoInput.trim() || todosLoading} className="btn btn-primary">
                Add
              </button>
            </div>

            {todos.length === 0 ? (
              <div className="workspace-empty">
                <p>No todos yet.</p>
                <p className="hint">Add tasks to track your progress on this angle.</p>
              </div>
            ) : (
              <div className="todo-list">
                {todos.map((todo) => (
                  <div key={todo.id} className="todo-item">
                    <input
                      type="checkbox"
                      checked={todo.completed}
                      onChange={() => handleToggleTodo(todo)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span style={{
                      flex: 1,
                      textDecoration: todo.completed ? 'line-through' : 'none',
                      opacity: todo.completed ? 0.6 : 1
                    }}>
                      {todo.content}
                    </span>
                    <button
                      onClick={() => handleDeleteTodo(todo.id)}
                      className="btn-icon"
                      style={{ color: 'var(--gray-400)', fontSize: '1.2rem' }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Resources Tab */}
      {activeTab === 'resources' && (
        <>
          <div className="workspace-content">
            <div style={{ marginBottom: '1rem' }}>
              <button
                onClick={() => setShowResourceForm(!showResourceForm)}
                className="btn btn-primary"
                style={{ width: '100%' }}
              >
                {showResourceForm ? 'Cancel' : '+ Add Resource'}
              </button>
            </div>

            {showResourceForm && (
              <div className="card" style={{ marginBottom: '1rem', padding: '1rem' }}>
                <div className="input-group">
                  <label className="label">Type</label>
                  <select
                    value={resourceForm.type}
                    onChange={(e) => setResourceForm({ ...resourceForm, type: e.target.value as 'link' | 'note' | 'file' })}
                    className="input"
                  >
                    <option value="note">Note</option>
                    <option value="link">Link</option>
                    <option value="file">File</option>
                  </select>
                </div>

                <div className="input-group">
                  <label className="label">Title</label>
                  <input
                    type="text"
                    value={resourceForm.title}
                    onChange={(e) => setResourceForm({ ...resourceForm, title: e.target.value })}
                    className="input"
                    placeholder="Resource title..."
                  />
                </div>

                {resourceForm.type === 'link' && (
                  <div className="input-group">
                    <label className="label">URL</label>
                    <input
                      type="url"
                      value={resourceForm.url}
                      onChange={(e) => setResourceForm({ ...resourceForm, url: e.target.value })}
                      className="input"
                      placeholder="https://..."
                    />
                  </div>
                )}

                {(resourceForm.type === 'note' || resourceForm.type === 'file') && (
                  <div className="input-group">
                    <label className="label">Content</label>
                    <textarea
                      value={resourceForm.content}
                      onChange={(e) => setResourceForm({ ...resourceForm, content: e.target.value })}
                      className="textarea"
                      placeholder="Add details..."
                      rows={4}
                    />
                  </div>
                )}

                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button onClick={handleAddResource} disabled={!resourceForm.title.trim() || resourcesLoading} className="btn btn-primary">
                    Save Resource
                  </button>
                  <button onClick={() => setShowResourceForm(false)} className="btn btn-secondary">
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {resources.length === 0 ? (
              <div className="workspace-empty">
                <p>No resources yet.</p>
                <p className="hint">Add links, notes, or files related to this angle.</p>
              </div>
            ) : (
              <div className="resource-list">
                {resources.map((resource) => (
                  <div key={resource.id} className="card" style={{ marginBottom: '0.75rem', padding: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                          <span className="badge badge-primary">{resource.type}</span>
                          <h4 style={{ fontSize: '0.95rem', fontWeight: '600' }}>{resource.title}</h4>
                        </div>
                        {resource.url && (
                          <a href={resource.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', fontSize: '0.85rem', wordBreak: 'break-all' }}>
                            {resource.url}
                          </a>
                        )}
                        {resource.content && (
                          <p style={{ color: 'var(--gray-600)', fontSize: '0.875rem', marginTop: '0.5rem', whiteSpace: 'pre-wrap' }}>
                            {resource.content}
                          </p>
                        )}
                        <p style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginTop: '0.5rem' }}>
                          {new Date(resource.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <button
                        onClick={() => handleDeleteResource(resource.id)}
                        className="btn-icon"
                        style={{ color: 'var(--gray-400)', fontSize: '1.2rem' }}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
    </>
  );
}
